// Cloudflare Turnstile — bot check gating the final wizard submit (step 4).
// Loaded and rendered explicitly (not the implicit page-load render) so the
// script and widget only cost anything once a visitor actually reaches the
// review step. `appearance: "interaction-only"` keeps it invisible unless
// Cloudflare decides the visitor needs a challenge.

export const TURNSTILE_SITE_KEY = "0x4AAAAAAEFK0f6ADA7QAxke";

export interface TurnstileApi {
  render: (container: HTMLElement, options: Record<string, unknown>) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<TurnstileApi> | null = null;

/** Injects the Turnstile script once (subsequent calls reuse the same load),
 *  resolving once `window.turnstile` is ready to render widgets. */
export function loadTurnstile(): Promise<TurnstileApi> {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve(window.turnstile);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
    script.async = true;
    script.defer = true;
    script.addEventListener("load", () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error("Turnstile script loaded but window.turnstile is missing."));
    });
    script.addEventListener("error", () => reject(new Error("Failed to load the Turnstile script.")));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

/** Polls `getToken` until it returns a non-empty value or `timeoutMs` elapses,
 *  then returns whatever it has (possibly still ""). Covers the gap between
 *  a submit-triggering event firing and the widget's async solve completing —
 *  callers with no button to gate (e.g. Aria's auto-submit) use this instead. */
export function waitForTurnstileToken(getToken: () => string, timeoutMs = 6000): Promise<string> {
  return new Promise((resolve) => {
    const start = Date.now();
    const tick = (): void => {
      const token = getToken();
      if (token || Date.now() - start >= timeoutMs) { resolve(token); return; }
      setTimeout(tick, 200);
    };
    tick();
  });
}
